import { getErrorDisplayMarkup } from "@context-providers/error-handler/error-handler-utils";
import { ActiveServer } from "@custom-types/server-types";
import { useToast } from "@flat-ui";
import { QueryParams } from "@router/route-params";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { UrlUtils } from "@stellar/utils";
import { useAppSelector } from "@store/store-helper";
import { ulmConfigClientSelector } from "@store/ulm-config/ulm-config-selector";
import { isValidRedirectUrl } from "@utils/browser-utils";
import { redirectClient } from "@utils/client-utils";
import { getUrlBasedOnServer } from "@utils/servers-utils";
import {
  getFaroCoreApiClientBasedOnServer,
  getRedirectUrlParam,
  getVisitedServerIdentifier,
  handleRedirectAfterLogin,
} from "@utils/ulm-utils";
import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

interface UseSphereDashboardLogin {
  /** Contains user visited server identifier */
  visitedServerIdentifier: FaroCoreApiTypes.ServerIdentifier | null;

  /** Login with email/password method to the selected server */
  loginWithEmail(props: LoginWithEmailProps): Promise<void>;

  /** Used to login with third party login provider */
  loginWithProvider(selectedServer: ActiveServer): void;
}

interface LoginWithEmailProps {
  /** The selected server to login */
  selectedServer: FaroCoreApiTypes.ServerIdentifier;

  /** Sanitized email address entered by user */
  email: string;

  /** Password value to perform login request */
  password: string;
}

/**
 * Custom hook that used for the login operations
 *
 * @returns login Method to perform login request
 */
export function useSphereDashboardLogin(): UseSphereDashboardLogin {
  const [searchParams] = useSearchParams();
  const client = useAppSelector(ulmConfigClientSelector);
  const { openToast } = useToast();

  /** Visited server identifier based on the url user is currently visiting */
  const visitedServerIdentifier = getVisitedServerIdentifier();

  const loginWithEmail = useCallback(
    async ({
      selectedServer,
      email,
      password,
    }: LoginWithEmailProps): Promise<void> => {
      const faroCoreApiClient =
        getFaroCoreApiClientBasedOnServer(selectedServer);

      await faroCoreApiClient.users.basicLogin({
        email,
        password,
      });

      const redirectUrlParam = getRedirectUrlParam(
        searchParams,
        selectedServer,
        client
      );

      // Only use the parent url param if the selected server is the same as the visited server
      // and the parent url param is valid according to the redirect whitelist
      const parentUrlParam = UrlUtils.readSearchParam({
        paramKey: QueryParams.parentUrl,
      });
      let parentUrl = null;
      if (
        visitedServerIdentifier === selectedServer &&
        parentUrlParam &&
        isValidRedirectUrl(parentUrlParam)
      ) {
        parentUrl = parentUrlParam;
      }

      // Give preference to the redirect URL param, then the parent URL param
      // If not provided then set to the default selected server URL
      const serverUrl =
        redirectUrlParam ||
        parentUrl ||
        getUrlBasedOnServer(selectedServer).dashboard2Url;

      try {
        handleRedirectAfterLogin(serverUrl, client, selectedServer);
      } catch (error) {
        openToast({
          title: "Invalid login URL",
          message: getErrorDisplayMarkup(error),
          variant: "error",
        });
      }
    },
    [client, openToast, searchParams, visitedServerIdentifier]
  );

  const loginWithProvider = useCallback(
    (selectedServer: ActiveServer) => {
      try {
        const loginUrl = new URL(selectedServer.loginProvider.loginUrl);

        const redirectUrlParam = getRedirectUrlParam(
          searchParams,
          selectedServer.identifier,
          client
        );

        // Only use the parent url param if the selected server is the same as the visited server
        // and the parent url param is valid according to the redirect whitelist
        const parentUrlParam = UrlUtils.readSearchParam({
          paramKey: QueryParams.parentUrl,
        });
        let parentUrl = null;
        if (
          visitedServerIdentifier === selectedServer.identifier &&
          parentUrlParam &&
          isValidRedirectUrl(parentUrlParam)
        ) {
          parentUrl = parentUrlParam;
        }

        // Give preference to the redirect URL param, then the parent URL param
        // If not provided then set to the default selected server URL
        const redirectUrl =
          redirectUrlParam ||
          parentUrl ||
          getUrlBasedOnServer(selectedServer.identifier).dashboard2Url;

        loginUrl.searchParams.set("redirectUrl", redirectUrl);

        redirectClient({
          client,
          redirectUrl: loginUrl.href,
        });
      } catch (error) {
        openToast({
          title: "Invalid login URL",
          message: getErrorDisplayMarkup(error),
          variant: "error",
        });
      }
    },
    [client, openToast, searchParams, visitedServerIdentifier]
  );

  return {
    visitedServerIdentifier,
    loginWithEmail,
    loginWithProvider,
  };
}
